import {
  type IconButtonProps,
  toast,
  useConfirmModal,
} from '@affine/component';
import { useConnectivityStatus } from '@affine/core/components/hooks/use-connectivity-status';
import { useSystemOnline } from '@affine/core/components/hooks/use-system-online';
import { toggleSelectionKey } from '@affine/core/components/selection-checkbox/range-selection';
import type { DocRecord } from '@affine/core/modules/doc';
import { CompatibleFavoriteItemsAdapter } from '@affine/core/modules/favorite';
import { GuardService } from '@affine/core/modules/permissions';
import { WorkbenchService } from '@affine/core/modules/workbench';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { UserFriendlyError } from '@affine/error';
import { useI18n } from '@affine/i18n';
import track from '@affine/track';
import {
  DeleteIcon,
  OpenInNewIcon,
  ResetIcon,
  SplitViewIcon,
} from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { memo, useCallback, useContext } from 'react';

import { useBlockSuiteMetaHelper } from '../../hooks/affine/use-block-suite-meta-helper';
import { useTrashDoc } from '../../hooks/affine/use-trash-doc';
import { IsFavoriteIcon } from '../../pure/icons';
import { DocExplorerContext } from '../context';
import {
  QuickActionIconButton,
  QuickSelectActionButton,
} from './quick-action-control';

export interface QuickActionProps extends IconButtonProps {
  doc: DocRecord;
}

export const QuickFavorite = memo(function QuickFavorite({
  doc,
  onClick,
  ...iconButtonProps
}: QuickActionProps) {
  const contextValue = useContext(DocExplorerContext);
  const quickFavorite = useLiveData(contextValue.quickFavorite$);

  const favAdapter = useService(CompatibleFavoriteItemsAdapter);
  const favourite = useLiveData(favAdapter.isFavorite$(doc.id, 'doc'));

  const toggleFavorite = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);
      e.stopPropagation();
      e.preventDefault();
      track.allDocs.list.docMenu.toggleFavorite();
      favAdapter.toggle(doc.id, 'doc');
    },
    [doc.id, favAdapter, onClick]
  );

  if (!quickFavorite) {
    return null;
  }

  return (
    <QuickActionIconButton
      icon={<IsFavoriteIcon favorite={favourite} />}
      onClick={toggleFavorite}
      data-testid="doc-list-operation-favorite"
      {...iconButtonProps}
    />
  );
});

export const QuickTab = memo(function QuickTab({
  doc,
  onClick,
  ...iconButtonProps
}: QuickActionProps) {
  const contextValue = useContext(DocExplorerContext);
  const quickTab = useLiveData(contextValue.quickTab$);
  const workbench = useService(WorkbenchService).workbench;
  const onOpenInNewTab = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);
      e.stopPropagation();
      e.preventDefault();
      track.allDocs.list.doc.openDoc();
      track.allDocs.list.docMenu.openInNewTab();
      workbench.openDoc(doc.id, { at: 'new-tab' });
    },
    [doc.id, onClick, workbench]
  );

  if (!quickTab) {
    return null;
  }

  return (
    <QuickActionIconButton
      onClick={onOpenInNewTab}
      icon={<OpenInNewIcon />}
      {...iconButtonProps}
    />
  );
});

export const QuickSplit = memo(function QuickSplit({
  doc,
  onClick,
  ...iconButtonProps
}: QuickActionProps) {
  const contextValue = useContext(DocExplorerContext);
  const quickSplit = useLiveData(contextValue.quickSplit$);
  const workbench = useService(WorkbenchService).workbench;

  const onOpenInSplitView = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);
      e.stopPropagation();
      e.preventDefault();
      track.allDocs.list.doc.openDoc();
      track.allDocs.list.docMenu.openInSplitView();
      workbench.openDoc(doc.id, { at: 'tail' });
    },
    [doc.id, onClick, workbench]
  );

  if (!quickSplit) {
    return null;
  }

  return (
    <QuickActionIconButton
      onClick={onOpenInSplitView}
      icon={<SplitViewIcon />}
      {...iconButtonProps}
    />
  );
});

export const QuickDelete = memo(function QuickDelete({
  doc,
  onClick,
  ...iconButtonProps
}: QuickActionProps) {
  const t = useI18n();
  const { openConfirmModal } = useConfirmModal();
  const contextValue = useContext(DocExplorerContext);
  const guardService = useService(GuardService);
  const trashDoc = useTrashDoc();
  const quickTrash = useLiveData(contextValue.quickTrash$);
  const isOnline = useSystemOnline();

  const onMoveToTrash = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);
      e.stopPropagation();
      e.preventDefault();
      if (!doc) {
        return;
      }

      track.allDocs.list.docMenu.deleteDoc();
      openConfirmModal({
        title: t['com.streamlain.moveToTrash.confirmModal.title'](),
        description: t['com.streamlain.moveToTrash.confirmModal.description']({
          title: doc.title$.value || t['Untitled'](),
        }),
        cancelText: t['com.streamlain.confirmModal.button.cancel'](),
        confirmText: t.Delete(),
        confirmButtonOptions: {
          variant: 'error',
        },
        onConfirm: async () => {
          try {
            const canTrash = await guardService.can('Doc_Trash', doc.id);
            if (!canTrash) {
              const msg = !isOnline
                ? t['com.streamlain.no-permission.offline']()
                : t['com.streamlain.no-permission']();
              toast(msg);
              return;
            }
            await trashDoc(doc);
          } catch (error) {
            console.error(error);
            const userFriendlyError = UserFriendlyError.fromAny(error);
            toast(t[`error.${userFriendlyError.name}`](userFriendlyError.data));
          }
        },
      });
    },
    [doc, guardService, isOnline, onClick, openConfirmModal, t, trashDoc]
  );

  if (!quickTrash) {
    return null;
  }

  return (
    <QuickActionIconButton
      onClick={onMoveToTrash}
      icon={<DeleteIcon />}
      variant="danger"
      data-testid="doc-list-operation-trash"
      {...iconButtonProps}
    />
  );
});

export const QuickSelect = memo(function QuickSelect({
  doc,
  onClick,
  ...iconButtonProps
}: QuickActionProps) {
  const contextValue = useContext(DocExplorerContext);
  const quickSelect = useLiveData(contextValue.quickSelect$);
  const selectedDocIds = useLiveData(contextValue.selectedDocIds$);

  const selected = selectedDocIds.includes(doc.id);

  const onChange = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      contextValue.selectMode$?.next(true);
      onClick?.(e);
      e.stopPropagation();
      e.preventDefault();
      contextValue.selectMode$?.next(true);
      contextValue.selectedDocIds$?.next(
        toggleSelectionKey({
          selectedKeys: selectedDocIds,
          targetKey: doc.id,
        }).selectedKeys
      );
    },
    [contextValue, doc.id, onClick, selectedDocIds]
  );

  if (!quickSelect) {
    return null;
  }

  return (
    <QuickSelectActionButton
      onClick={onChange}
      checked={selected}
      {...iconButtonProps}
    />
  );
});

export const QuickDeletePermanently = memo(function QuickDeletePermanently({
  doc,
  onClick,
  style,
  ...iconButtonProps
}: QuickActionProps) {
  const t = useI18n();
  const guardService = useService(GuardService);
  const contextValue = useContext(DocExplorerContext);
  const { permanentlyDeletePage } = useBlockSuiteMetaHelper();
  const actionOverrides = useLiveData(contextValue.actionOverrides$);
  const workspace = useService(WorkspaceService).workspace;
  const connectivityStatus = useConnectivityStatus();
  const destructiveLifecycleOffline =
    workspace.flavour !== 'local' && !connectivityStatus.backendAvailable;
  const quickDeletePermanently = useLiveData(
    contextValue.quickDeletePermanently$
  );
  const { openConfirmModal } = useConfirmModal();
  const isOnline = useSystemOnline();

  const handleDeletePermanently = useCallback(async () => {
    try {
      const can = await guardService.can('Doc_Delete', doc.id);
      if (!can) {
        // sl-o0ra: Differentiate offline vs. no-permission
        const msg = !isOnline
          ? t['com.streamlain.no-permission.offline']()
          : t['com.streamlain.no-permission']();
        toast(msg);
        return;
      }
      const deleted = await permanentlyDeletePage(doc.id);
      if (deleted) {
        toast(t['com.streamlain.toastMessage.permanentlyDeleted']());
      }
    } catch (e) {
      console.error('Failed to permanently delete doc:', e);
      toast('Failed to permanently delete doc');
    }
  }, [doc.id, guardService, isOnline, permanentlyDeletePage, t]);

  const handleConfirmDeletePermanently = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);
      e.stopPropagation();
      e.preventDefault();
      if (actionOverrides.onDelete) {
        actionOverrides.onDelete([doc.id]);
        return;
      }
      if (destructiveLifecycleOffline) {
        void permanentlyDeletePage(doc.id).catch(e => {
          console.error('Failed to permanently delete doc:', e);
          toast('Failed to permanently delete doc');
        });
        return;
      }
      openConfirmModal({
        title: `${t['com.streamlain.trashOperation.deletePermanently']()}?`,
        description: t['com.streamlain.trashOperation.deleteDescription'](),
        cancelText: t['Cancel'](),
        confirmText: t['com.streamlain.trashOperation.delete'](),
        confirmButtonOptions: {
          variant: 'error',
        },
        onConfirm: () => void handleDeletePermanently(),
      });
    },
    [
      destructiveLifecycleOffline,
      actionOverrides,
      doc.id,
      handleDeletePermanently,
      onClick,
      openConfirmModal,
      permanentlyDeletePage,
      t,
    ]
  );

  if (!quickDeletePermanently) {
    return null;
  }

  return (
    <QuickActionIconButton
      data-testid="delete-page-button"
      onClick={handleConfirmDeletePermanently}
      icon={<DeleteIcon />}
      variant="danger"
      aria-disabled={destructiveLifecycleOffline || undefined}
      style={destructiveLifecycleOffline ? { ...style, opacity: 0.55 } : style}
      {...iconButtonProps}
    />
  );
});

export const QuickRestore = memo(function QuickRestore({
  doc,
  onClick,
  style,
  ...iconButtonProps
}: QuickActionProps) {
  const t = useI18n();
  const contextValue = useContext(DocExplorerContext);
  const quickRestore = useLiveData(contextValue.quickRestore$);
  const actionOverrides = useLiveData(contextValue.actionOverrides$);
  const { restoreFromTrash } = useBlockSuiteMetaHelper();
  const guardService = useService(GuardService);
  const workspace = useService(WorkspaceService).workspace;
  const connectivityStatus = useConnectivityStatus();
  const destructiveLifecycleOffline =
    workspace.flavour !== 'local' && !connectivityStatus.backendAvailable;
  const isOnline = useSystemOnline();

  const handleRestore = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);
      e.stopPropagation();
      e.preventDefault();
      if (actionOverrides.onRestore) {
        actionOverrides.onRestore([doc.id]);
        return;
      }
      if (destructiveLifecycleOffline) {
        restoreFromTrash(doc.id);
        return;
      }
      guardService
        .can('Doc_Delete', doc.id)
        .then(can => {
          if (can) {
            restoreFromTrash(doc.id);
            toast(
              t['com.streamlain.toastMessage.restored']({
                title: doc.title$.value || 'Untitled',
              })
            );
          } else {
            const msg = !isOnline
              ? t['com.streamlain.no-permission.offline']()
              : t['com.streamlain.no-permission']();
            toast(msg);
          }
        })
        .catch(e => {
          console.error(e);
        });
    },
    [
      destructiveLifecycleOffline,
      actionOverrides,
      doc.id,
      doc.title$,
      guardService,
      isOnline,
      onClick,
      restoreFromTrash,
      t,
    ]
  );

  if (!quickRestore) {
    return null;
  }

  return (
    <QuickActionIconButton
      data-testid="restore-page-button"
      onClick={handleRestore}
      icon={<ResetIcon />}
      aria-disabled={destructiveLifecycleOffline || undefined}
      style={destructiveLifecycleOffline ? { ...style, opacity: 0.55 } : style}
      {...iconButtonProps}
    />
  );
});
